import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, PauseCircle, PlayCircle } from "lucide-react";

const OPTIONS = [
    { key: "proactive_notifications", label: "Notifiche proattive agli agenti", desc: "L'AI analizza il CRM ogni ora e avvisa via email l'agente assegnato quando un cliente non viene contattato da troppi giorni o un lead prioritario è fermo." },
    { key: "auto_emails", label: "Invio email automatiche ai clienti", desc: "L'AI scrive ai clienti trascurati per conto dell'agenzia (mittente: Assistente Virtuale VendiDirect) per mantenere vivo il contatto." },
    { key: "auto_appointments", label: "Presa appuntamenti automatica", desc: "Le richieste di visita in attesa entro 48 ore vengono confermate automaticamente: il cliente riceve la conferma e l'agente viene avvisato con promemoria." },
    { key: "auto_property_search", label: "Ricerca immobili automatica", desc: "Quando il monitoraggio trova nuovi annunci di privati ad alta affidabilità sui portali esterni, l'AI ti avvisa subito via email." },
    { key: "commercial_suggestions", label: "Suggerimenti commerciali", desc: "L'AI suggerisce all'agente la mossa migliore per ogni lead prioritario fermo (chiamata, selezione immobili, chiusura trattativa)." },
];

const Toggle = ({ on, onClick, testid }) => (
    <button data-testid={testid} onClick={onClick} role="switch" aria-checked={on}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-gold" : "bg-ink-700"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink-950 transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </button>
);

export default function AiAssistantSettings() {
    const [s, setS] = useState(null);

    useEffect(() => {
        api.get("/crm/ai-settings").then((r) => setS(r.data)).catch((e) => console.error(e));
    }, []);

    const toggle = async (key) => {
        const next = { ...s, [key]: !s[key] };
        setS(next);
        try { await api.put("/crm/ai-settings", { [key]: next[key] }); }
        catch (e) { setS(s); }
    };
    const setDays = async (v) => {
        const days = Math.max(1, Math.min(90, parseInt(v) || 7));
        setS({ ...s, days_no_contact: days });
        try { await api.put("/crm/ai-settings", { days_no_contact: days }); } catch (e) { console.error(e); }
    };

    if (!s) return null;

    return (
        <div data-testid="ai-settings-section" className="space-y-5">
            <div className="border border-gold/30 bg-ink-900/40 p-5">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gold mb-1"><Sparkles size={15} /> Assistente AI · Autonomia</div>
                <p className="text-xs text-ink-400 mb-4">Decidi cosa l'assistente può fare in autonomia per la tua agenzia. Ogni azione automatica viene registrata e tracciata.</p>
                <div data-testid="ai-pause-row" className={`mb-4 flex items-start justify-between gap-4 border p-4 ${s.paused ? "border-amber-400/40 bg-amber-400/10" : "border-white/10 bg-ink-900/60"}`}>
                    <div>
                        <div className="flex items-center gap-2 text-sm text-ink-50">
                            {s.paused ? <PauseCircle size={15} className="text-amber-400" /> : <PlayCircle size={15} className="text-emerald-400" />}
                            {s.paused ? "Autopilot in pausa" : "Autopilot attivo"}
                        </div>
                        <div className="text-xs text-ink-400 mt-0.5 max-w-xl">
                            {s.paused
                                ? "Nessuna analisi automatica in background: le funzioni qui sotto ripartono solo quando riattivi l'Autopilot a mano."
                                : "L'assistente analizza il CRM ogni ora secondo le autonomie attivate qui sotto."}
                            {s.is_demo && " Sugli account demo l'Autopilot è in pausa di default per non consumare crediti IA."}
                        </div>
                    </div>
                    <button
                        data-testid="ai-pause-toggle"
                        onClick={() => toggle("paused")}
                        className={`shrink-0 px-4 py-2 text-xs font-medium transition-colors ${s.paused ? "bg-gold text-black hover:bg-gold-soft" : "border border-white/15 text-ink-300 hover:text-amber-300 hover:border-amber-400/40"}`}
                    >
                        {s.paused ? "Riattiva Autopilot" : "Metti in pausa"}
                    </button>
                </div>
                <div className={`divide-y divide-white/5 ${s.paused ? "opacity-50" : ""}`}>
                    {OPTIONS.map((o) => (
                        <div key={o.key} className="py-3.5 flex items-start justify-between gap-4">
                            <div>
                                <div className="text-sm text-ink-50">{o.label}</div>
                                <div className="text-xs text-ink-400 mt-0.5 max-w-xl">{o.desc}</div>
                            </div>
                            <Toggle on={!!s[o.key]} onClick={() => toggle(o.key)} testid={`ai-toggle-${o.key}`} />
                        </div>
                    ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-ink-400">Considera un cliente "non contattato" dopo</span>
                    <input data-testid="ai-days-no-contact" type="number" min={1} max={90} value={s.days_no_contact}
                        onChange={(e) => setDays(e.target.value)}
                        className="w-20 bg-transparent border border-white/15 px-3 py-1.5 text-sm text-ink-50 focus:border-gold outline-none" />
                    <span className="text-xs text-ink-400">giorni senza attività</span>
                </div>
            </div>
        </div>
    );
}
